import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { UserProfileModel } from './models/user-profile.model';
import { plainToClass } from 'class-transformer';

import { AuthService as SocialAuthService, FacebookLoginProvider } from 'angularx-social-login';
import { GoogleLoginProvider } from 'angularx-social-login';
import { ProfileCheckModel } from './models/profile-check.model';
import { EnvironmentSettingsModel } from './models/environment-settings.model';
import { SignUpModel } from './models/sign-up.model';
import { GoogleLoginModel } from './models/google-login.model';
import { FacebookLoginModel } from './models/facebook-login.model';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BiodigitalLoginModel } from './models/biodigital-login.model';
import { UserAppModel } from './models/user-app.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private socialAuthService: SocialAuthService
  ) {
    
  }

  loginWithBiodigital(login: BiodigitalLoginModel) {
    return this.http.post('/ws/user/sign/in', BiodigitalLoginModel.toJSON(login));
  }

  loginWithGoogle(login: GoogleLoginModel) {
    return this.http.post('/ws/user/sign/in/google', GoogleLoginModel.toJSON(login));
  }

  // Deprecated
  loginWithFacebook(login: FacebookLoginModel) {
    return this.http.post('/ws/user/sign/in/facebook', FacebookLoginModel.toJSON(login));
  }

  logout() {
    return this.http.get('/ws/user/sign/out');
  }

  checkProfile(email: string): Observable<ProfileCheckModel> {
    return this.http.post('/ws/user/profile/check', { version: 2, email: email })
      .pipe(
        map((result: any) => {
          const profileCheck = ProfileCheckModel.fromJSON(result);
          profileCheck.isThirdParty = profileCheck.thirdPartyProvider !== '';
          return profileCheck;
        })
      );
  }

  getUserProfile(): Observable<UserProfileModel> {
    return this.http.get('/ws/user/profile')
      .pipe(
        map((result: any) => {
          result.organizations = JSON.parse(result.organizations);
          return plainToClass(UserProfileModel, result as Object);
        })
      );
  }

  getEnvironmentSettings(): Observable<EnvironmentSettingsModel> {
    return this.http.get('/ws/environment/settings?version=2')
      .pipe(
        map((result: any) => {
          return EnvironmentSettingsModel.fromJSON(result);
        })
      );
  }

  /**
   * Gets a list of user apps
   */
  getUserApps(): Observable<UserAppModel[]> {
    return this.http.get('/ws/developer/apps/profile')
      .pipe(
        map((result: any) => result && result.apps ? JSON.parse(result.apps) : [])
      );
  }

  getGroupConfigFromRoute(snapshot: ActivatedRouteSnapshot) {
    const group = snapshot.queryParams.group || '';
    const groupCode = snapshot.queryParams.code || '';
    const invitation = snapshot.queryParams.group_invite || '';

    const config = {
      group: group,
      groupCode: groupCode,
      groupCodeMode: group !== '' ? 1 : 0,
      groupInviteMode: invitation !== '' ? 1 : 0,
      groupInviteKey: invitation
    };

    return config;
  }

  isLoggedIn() {
    // Uses version 2, which responds with 200 status for good, 401 if not
    return this.http.get('/ws/user/is/signed-in?version=2');
  }

  getProfileRestrictions(profile: UserProfileModel): {email: boolean; ip: boolean, organizationName: string} {
    let emailRestricted = false;
    let ipRestricted = false;
    let name = '';

    profile.organizations.forEach(organization => {
      if (emailRestricted || ipRestricted) {
        return;
      }

      name = organization.name;

      emailRestricted = 
        organization.user_is_subscribed && 
        organization.email_domain_restriction && 
        !organization.user_has_email_domain_access;

      ipRestricted = 
        organization.user_is_subscribed && 
        organization.ip_address_restriction &&
        !organization.user_has_domain_access;

      if (emailRestricted || ipRestricted) {

      }
    });

    return {
      organizationName: name,
      email: emailRestricted,
      ip: ipRestricted
    };
  }

  resetPassword(email: string) {
    return this.http.post('/ws/user/password/forgot', { email: email });
  }

  signUp(value: SignUpModel) {
    return this.http.post('/ws/user/sign/up', SignUpModel.toJSON(value) );
  }

  authenticateWithGoogle() {
    return this.socialAuthService.signIn(GoogleLoginProvider.PROVIDER_ID);
  }

  authenticateWithFacebook() {
    return this.socialAuthService.signIn(FacebookLoginProvider.PROVIDER_ID);
  }
  
}
